import { Link } from "react-router-dom";
import { Heart, Compass, Shield, Users, ArrowRight, Sparkles } from "lucide-react";

const VALUES = [
  {
    icon: <Heart className="w-6 h-6" fill="currentColor" />,
    title: "Your story matters",
    desc: "Every version of it. The one you tell, the one you hide, the one you haven't found words for yet.",
  },
  {
    icon: <Shield className="w-6 h-6" />,
    title: "Healing is a right",
    desc: "Not a privilege. Not a subscription tier. Not something reserved for people with the right insurance card.",
  },
  {
    icon: <Users className="w-6 h-6" />,
    title: "Care across generations",
    desc: "Trauma is inherited. Healing can be, too. We're building tools that reach backward, forward, and sideways.",
  },
  {
    icon: <Compass className="w-6 h-6" />,
    title: "Technology that serves humans",
    desc: "HIPAA-compliant infrastructure, patented workflows, and AI — all in service of the people using them, never the other way around.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page" className="bg-white">
      {/* HERO */}
      <section className="relative py-20 lg:py-28 bg-gradient-hero" data-testid="about-hero">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <p className="eyebrow mb-5">Our story</p>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-navy">
            A movement to <span className="hl-teal">break generational cycles</span> of trauma.
          </h1>
          <p className="mt-8 text-lg sm:text-xl text-muted-ink leading-relaxed max-w-2xl mx-auto">
            Generational Health exists because crisis shouldn't have a waitlist, and healing shouldn't be
            reserved for the already-well-resourced. We're building the mental health platform our families wish existed.
          </p>
        </div>
      </section>

      {/* MISSION */}
      <section className="py-20 lg:py-28" data-testid="about-mission">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="bg-navy text-white rounded-[2.5rem] p-10 lg:p-16 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-96 h-96 bg-teal/15 rounded-full blur-3xl" aria-hidden="true" />
            <div className="relative">
              <p className="eyebrow !text-teal mb-5">Our mission</p>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mb-7 text-white">
                To make immediate, human mental health support available to <span className="hl-teal">anyone</span> —
                regardless of cost, zip code, or hour.
              </h2>
              <p className="text-lg text-white/80 leading-relaxed max-w-3xl">
                Generational Health is a next-generation mental health platform providing real-time,
                trauma-informed support to individuals, families, and communities — especially those who have been
                historically underserved. We pair a HIPAA-compliant mobile app with licensed clinicians, a patented
                provider workflow, an AI companion, and an integrated clinical stack (pVerify, DoseSpot, Drug Bank, DocuSign).
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-20 lg:py-28 bg-cream" data-testid="about-values">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-14">
            <p className="eyebrow mb-4">What we believe</p>
            <h2 className="font-display text-4xl sm:text-5xl text-navy leading-[1.05]">Four beliefs run through <span className="hl-teal">everything</span> we build.</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {VALUES.map((v, i) => (
              <div key={i} className="gh-card" data-testid={`value-card-${i}`}>
                <div className="w-12 h-12 rounded-xl bg-teal-light text-teal flex items-center justify-center mb-5">
                  {v.icon}
                </div>
                <h3 className="font-display text-2xl mb-2 text-navy">{v.title}</h3>
                <p className="text-muted-ink leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TAGLINE */}
      <section className="py-24" data-testid="about-tagline">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
          <Sparkles className="w-10 h-10 text-teal mx-auto mb-6" />
          <p className="font-display text-5xl sm:text-6xl text-navy leading-[1.05]">
            Your mind. <span className="hl-teal">Your power.</span>
          </p>
          <p className="mt-6 text-lg text-muted-ink">
            That's our promise — and the whole reason this exists.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/download" className="btn-primary" data-testid="about-download-cta">
              Download the app <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/partner" className="btn-outline" data-testid="about-partner-cta">
              Partner with us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
